/**
 * 미리보기 문서 일반화 (chat-doc-preview-formats — 2026-07-01, 프론트 단독·additive).
 *
 * 기존 마크다운 전용 미리보기([[markdown-doc]])를 확장해 pdf/docx/xlsx 첨부까지 우측 패널에서
 * 미리보기한다. presigned URL 이 앱과 동일 출처(axi.tchain.cloud, edge 프록시)라 파일 바이트를
 * fetch → ArrayBuffer 로 받아 클라이언트 렌더 라이브러리(pdfjs/docx-preview/xlsx)로 그린다.
 * (Content-Disposition:attachment 는 fetch 엔 무관 — 브라우저 네비게이션만 막음.)
 *
 * - markdown: 첨부 .md + 본문 내 .md URL (기존 로직 재사용).
 * - pdf/docx/xlsx/pptx: **첨부만** (임의 URL office 문서는 CORS 로 바이트 fetch 불가).
 * - pptx 는 chat-pptx-preview(2026-07-09)에서 추가 — @aiden0z/pptx-renderer.
 * - hwp/hwpx 도 **채팅에서 바로 열린다**(2026-08-14). 전에는 워크스페이스 편집기 전용이었다 —
 *   내용을 보려면 rhwp-studio 셸(1.3MB)을 띄우는 수밖에 없었고 그건 채팅에 붙일 수 없었다
 *   (문서 리스너·beforeunload·전역 테마를 건드려 호스트를 오염시킨다). 읽기 전용 경량
 *   렌더러(`@workchat/office/hwp-preview`)가 생기면서 그 제약이 사라졌다.
 */
import { extractMarkdownDocs, isMarkdownAttachment } from './markdown-doc';
import { narrowAttachments } from './attachments';
import { requestPresignedDownload } from '../api/files';
import type { ChatMessageEvent } from '../types/chat';
import type { MessageAttachment } from '../types/api';

export type PreviewKind = 'markdown' | 'html' | 'pdf' | 'docx' | 'xlsx' | 'pptx' | 'hwp';
/**
 * 채팅 첨부 미리보기가 다루는 형식 — 바이트를 받아 클라이언트가 그리는 것들.
 * markdown·html 은 텍스트라 별도 경로(`MarkdownContent`·`HtmlContent`)를 탄다.
 */
export type OfficePreviewKind = Exclude<PreviewKind, 'markdown' | 'html'>;

/**
 * html 첨부 판별 (chat-html-preview — 2026-07-18, iOS #230 / AOS #231 웹 파리티).
 * ⚠ 확장자 우선 — 모바일이 업로드 시 stored-XSS 차단(text/html)을 피하려고 content-type 을
 *   text/plain 으로 안전화하므로 MIME 이 text/html 이 아닐 수 있다. text/html·xhtml 은 폴백.
 */
const HTML_EXT_RE = /\.(html?|xhtml)(?:[?#].*)?$/i;
export function isHtmlAttachment(a: MessageAttachment): boolean {
  if (HTML_EXT_RE.test(a.name ?? '')) return true;
  const mime = (a.mimeType ?? '').toLowerCase().split(';')[0]!.trim();
  return mime === 'text/html' || mime.startsWith('application/xhtml');
}

/** 미리보기 가능한 단일 문서 descriptor (markdown-doc 의 MarkdownDoc 를 kind 로 일반화). */
export interface PreviewDoc {
  /** 메시지 내 안정 키 (미리보기 store + react key). */
  key: string;
  /** 표시용 제목 (파일명 또는 URL 마지막 세그먼트). */
  title: string;
  kind: PreviewKind;
  source:
    | { kind: 'attachment'; fileId: string }
    | { kind: 'url'; url: string };
}

const EXT_KIND: Array<[RegExp, OfficePreviewKind]> = [
  [/\.pdf(?:[?#].*)?$/i, 'pdf'],
  [/\.docx(?:[?#].*)?$/i, 'docx'],
  [/\.xlsx(?:[?#].*)?$/i, 'xlsx'],
  [/\.pptx(?:[?#].*)?$/i, 'pptx'],
  // ⚠ 확장자 폴백이 특히 중요하다 — 업로드 declare 는 2026-08-14(PR#408)부터라, 그 전에
  //   올라간 .hwp/.hwpx 첨부는 DB 에 `application/octet-stream` 으로 남아 있다.
  //   (워크스페이스 판정 `workspace-preview.ts` 과 같은 이유·같은 규칙.)
  [/\.hwpx?(?:[?#].*)?$/i, 'hwp'],
];

const MIME_KIND: Record<string, OfficePreviewKind> = {
  'application/pdf': 'pdf',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document': 'docx',
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': 'xlsx',
  'application/vnd.openxmlformats-officedocument.presentationml.presentation': 'pptx',
  // 한컴 한글 — 업로드 허용 목록(files.routes.ts ALLOWED_DOCUMENT_MIMES)과 같은 집합.
  'application/x-hwp': 'hwp',
  'application/haansofthwp': 'hwp',
  'application/vnd.hancom.hwp': 'hwp',
  'application/hwp+zip': 'hwp',
  'application/vnd.hancom.hwpx': 'hwp',
};

/** 첨부가 pdf/docx/xlsx/pptx 면 해당 kind, 아니면 null (mime 우선 → 확장자 폴백). */
export function officePreviewKind(a: MessageAttachment): OfficePreviewKind | null {
  const mime = (a.mimeType ?? '').toLowerCase().split(';')[0]!.trim();
  if (MIME_KIND[mime]) return MIME_KIND[mime]!;
  const name = a.name ?? '';
  for (const [re, kind] of EXT_KIND) {
    if (re.test(name)) return kind;
  }
  return null;
}

/**
 * 메시지에서 미리보기 가능한 문서 목록. markdown(첨부+URL) + pdf/docx/xlsx(첨부).
 * 삭제(톰스톤) 메시지는 본문/첨부 블랭크라 자연히 빈 배열.
 */
export function extractPreviewDocs(message: ChatMessageEvent): PreviewDoc[] {
  const docs: PreviewDoc[] = [];
  // 1) 마크다운 — 기존 로직 재사용 (kind 부여).
  for (const md of extractMarkdownDocs(message)) {
    docs.push({ ...md, kind: 'markdown' });
  }
  // 2) office/pdf/html 첨부.
  for (const a of narrowAttachments(message.attachments)) {
    const kind = officePreviewKind(a);
    if (kind) {
      docs.push({
        key: `att:${a.fileId}`,
        title: a.name ?? `document.${kind}`,
        kind,
        source: { kind: 'attachment', fileId: a.fileId },
      });
      continue;
    }
    if (isHtmlAttachment(a)) {
      docs.push({
        key: `att:${a.fileId}`,
        title: a.name ?? 'document.html',
        kind: 'html',
        source: { kind: 'attachment', fileId: a.fileId },
      });
    }
  }
  return docs;
}

/**
 * 단일 첨부 → 미리보기 문서 descriptor (chat-file-action-modal — 2026-07-02, additive).
 * markdown(.md) 또는 office(pdf/docx/xlsx/pptx) 면 PreviewDoc, 그 외(zip/hwp 등)는 null.
 * 파일 카드 클릭 시 미리보기 가능 여부 판정 + openPreview 에 사용(불가 시 경고 모달). extractPreviewDocs
 * 와 동일한 key(`att:<fileId>`)·kind 규칙을 재사용해 미리보기 패널 활성 하이라이트와 일관.
 */
export function attachmentPreviewDoc(a: MessageAttachment): PreviewDoc | null {
  if (isMarkdownAttachment(a)) {
    return {
      key: `att:${a.fileId}`,
      title: a.name ?? 'document.md',
      kind: 'markdown',
      source: { kind: 'attachment', fileId: a.fileId },
    };
  }
  const kind = officePreviewKind(a);
  if (kind) {
    return {
      key: `att:${a.fileId}`,
      title: a.name ?? `document.${kind}`,
      kind,
      source: { kind: 'attachment', fileId: a.fileId },
    };
  }
  if (isHtmlAttachment(a)) {
    return {
      key: `att:${a.fileId}`,
      title: a.name ?? 'document.html',
      kind: 'html',
      source: { kind: 'attachment', fileId: a.fileId },
    };
  }
  return null;
}

/**
 * 첨부 fileId → presigned URL → ArrayBuffer (동일 출처 fetch). office/pdf 렌더러 공통.
 * 실패 시 throw (호출부가 에러 표시).
 */
export async function fetchAttachmentBytes(fileId: string): Promise<ArrayBuffer> {
  // 인앱 문서 미리보기 = 「열람」(§3.4.1)
  const res = await requestPresignedDownload(fileId, { purpose: 'preview' });
  const r = await fetch(res.downloadUrl);
  if (!r.ok) throw new Error(`HTTP ${r.status}`);
  return await r.arrayBuffer();
}

/**
 * 첨부 fileId → presigned URL → text (동일 출처 fetch). html 미리보기 등 텍스트 문서용.
 * 실패 시 throw (호출부가 에러 표시).
 */
export async function fetchAttachmentText(fileId: string): Promise<string> {
  // 인앱 텍스트 · HTML 미리보기 = 「열람」(§3.4.1)
  const res = await requestPresignedDownload(fileId, { purpose: 'preview' });
  const r = await fetch(res.downloadUrl);
  if (!r.ok) throw new Error(`HTTP ${r.status}`);
  return await r.text();
}
